<template>
    <section class="blocked-users">
        <div class="blocked-users__container container-fluid">
            <!-- Page header -->
            <header class="blocked-users__header">
                <h2 class="blocked-users__title">
                    <button
                        type="button"
                        class="btn back_btn"
                        aria-label="Go back"
                        @click="goBack"
                    >
                        <i
                            class="fa fa-chevron-left"
                            aria-hidden="true"
                        ></i>
                    </button>

                    <span>Blocked Users</span>
                </h2>
            </header>

            <!-- Initial loader -->
            <div
                v-if="isInitialLoading"
                class="blocked-users__loader"
                role="status"
                aria-live="polite"
            >
                <div class="spinner spinner-1"></div>

                <span class="sr-only">
                    Loading blocked users
                </span>
            </div>

            <!-- Error state -->
            <div
                v-else-if="loadError"
                class="blocked-users__error"
                role="alert"
            >
                <p class="blocked-users__error-message">
                    {{ loadError }}
                </p>

                <button
                    type="button"
                    class="btn btn-info"
                    @click="loadBlockedUsers"
                >
                    Retry
                </button>
            </div>

            <!-- Blocked-users table -->
            <div
                v-else
                class="
                    blocked-users__table-wrapper
                    table-responsive
                "
            >
                <table
                    class="
                        blocked-users__table
                        table
                        meeting_main_table
                    "
                >
                    <thead>
                        <tr>
                            <th scope="col">
                                Name
                            </th>

                            <th
                                scope="col"
                                class="text-right"
                            >
                                Action
                            </th>
                        </tr>
                    </thead>

                    <tbody v-if="hasBlockedUsers">
                        <tr
                            v-for="blockedItem in blockedUsers"
                            :key="getBlockedItemKey(blockedItem)"
                            class="blocked-users__row"
                        >
                            <td data-label="Name">
                                <div class="blocked-user">
                                    <img
                                        :src="
                                            getUserThumbnail(
                                                blockedItem.blocked_user
                                            )
                                        "
                                        :alt="
                                            getUserName(
                                                blockedItem.blocked_user
                                            )
                                        "
                                        class="blocked-user__avatar"
                                        width="46"
                                        height="46"
                                        @error="handleAvatarError"
                                    >

                                    <span class="blocked-user__name">
                                        {{
                                            getUserName(
                                                blockedItem.blocked_user
                                            )
                                        }}
                                    </span>
                                </div>
                            </td>

                            <td
                                data-label="Action"
                                class="
                                    blocked-users__action-cell
                                    text-right
                                "
                            >
                                <button
                                    type="button"
                                    class="blocked-users__unblock"
                                    :disabled="
                                        isUnblocking(
                                            blockedItem.id
                                        )
                                    "
                                    @click="
                                        openUnblockConfirmation(
                                            blockedItem
                                        )
                                    "
                                >
                                    <i
                                        v-if="
                                            isUnblocking(
                                                blockedItem.id
                                            )
                                        "
                                        class="
                                            fa
                                            fa-spinner
                                            fa-spin
                                        "
                                        aria-hidden="true"
                                    ></i>

                                    <span>
                                        {{
                                            isUnblocking(
                                                blockedItem.id
                                            )
                                                ? 'Unblocking...'
                                                : 'Unblock'
                                        }}
                                    </span>
                                </button>
                            </td>
                        </tr>

                        <!-- Load more -->
                        <tr v-if="nextPageUrl">
                            <td
                                colspan="2"
                                class="
                                    blocked-users__load-more-cell
                                "
                            >
                                <button
                                    type="button"
                                    class="
                                        blocked-users__load-more
                                    "
                                    :disabled="isLoadingMore"
                                    @click="loadMoreBlockedUsers"
                                >
                                    <i
                                        v-if="isLoadingMore"
                                        class="
                                            fa
                                            fa-spinner
                                            fa-spin
                                        "
                                        aria-hidden="true"
                                    ></i>

                                    <span>
                                        {{
                                            isLoadingMore
                                                ? 'Loading...'
                                                : 'Load More'
                                        }}
                                    </span>
                                </button>
                            </td>
                        </tr>
                    </tbody>

                    <!-- Empty state -->
                    <tbody v-else>
                        <tr>
                            <td
                                colspan="2"
                                class="blocked-users__empty"
                            >
                                You don't have any blocked users.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Unblock confirmation popup -->
        <div
            v-if="isConfirmationOpen"
            class="unblock-confirmation"
            role="dialog"
            aria-modal="true"
            aria-labelledby="unblock-confirmation-title"
            aria-describedby="unblock-confirmation-message"
            @click.self="closeUnblockConfirmation"
        >
            <div
                ref="confirmationDialog"
                class="unblock-confirmation__dialog"
            >
                <div class="unblock-confirmation__body">
                    <div
                        v-if="selectedBlockedUser"
                        class="unblock-confirmation__user"
                    >
                        <img
                            :src="
                                getUserThumbnail(
                                    selectedBlockedUser
                                )
                            "
                            :alt="
                                selectedBlockedUserName
                            "
                            class="
                                unblock-confirmation__avatar
                            "
                            width="64"
                            height="64"
                            @error="handleAvatarError"
                        >
                    </div>

                    <p
                        id="unblock-confirmation-message"
                        class="unblock-confirmation__message"
                    >
                        Are you sure you want to unblock
                        <strong>
                            {{ selectedBlockedUserName }}
                        </strong>?
                    </p>

                    <p class="unblock-confirmation__description">
                        This user will be able to contact you
                        again after being unblocked.
                    </p>
                </div>

                <footer class="unblock-confirmation__footer">
                    <button
                        type="button"
                        class="
                            unblock-confirmation__button
                            unblock-confirmation__button--cancel
                        "
                        :disabled="isConfirmationSubmitting"
                        @click="closeUnblockConfirmation"
                    >
                        Cancel
                    </button>

                    <button
                        ref="confirmButton"
                        type="button"
                        class="
                            unblock-confirmation__button
                            unblock-confirmation__button--confirm
                        "
                        :disabled="isConfirmationSubmitting"
                        @click="confirmUnblockUser"
                    >
                        <i
                            v-if="isConfirmationSubmitting"
                            class="
                                fa
                                fa-spinner
                                fa-spin
                            "
                            aria-hidden="true"
                        ></i>

                        <span>
                            {{
                                isConfirmationSubmitting
                                    ? 'Unblocking...'
                                    : 'Yes, Unblock'
                            }}
                        </span>
                    </button>
                </footer>
            </div>
        </div>
    </section>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import toast from '@/services/toast';

const DEFAULT_AVATAR = require(
    '@/assets/images/user2.png'
);

export default {
    name: 'BlockedUserList',

    data() {
        return {
            isInitialLoading: false,
            isLoadingMore: false,
            loadError: '',

            additionalBlockedUsers: [],
            nextPageOverride: null,

            isConfirmationOpen: false,
            selectedBlockedItem: null,
            unblockingUserId: null,

            previouslyFocusedElement: null
        };
    },

    computed: {
        ...mapGetters({
            blockUserState: 'getBlockGetters'
        }),

        blockUserData() {
            if (
                !this.blockUserState ||
                !this.blockUserState.data
            ) {
                return null;
            }

            return this.blockUserState.data;
        },

        storeBlockedUsers() {
            if (
                !this.blockUserData ||
                !Array.isArray(
                    this.blockUserData.results
                )
            ) {
                return [];
            }

            return this.blockUserData.results;
        },

        blockedUsers() {
            return this.mergeBlockedUsers(
                this.storeBlockedUsers,
                this.additionalBlockedUsers
            );
        },

        hasBlockedUsers() {
            return this.blockedUsers.length > 0;
        },

        nextPageUrl() {
            if (this.nextPageOverride !== null) {
                return this.nextPageOverride;
            }

            if (
                this.blockUserData &&
                this.blockUserData.next
            ) {
                return this.blockUserData.next;
            }

            return '';
        },

        selectedBlockedUser() {
            if (
                !this.selectedBlockedItem ||
                !this.selectedBlockedItem.blocked_user
            ) {
                return null;
            }

            return this.selectedBlockedItem.blocked_user;
        },

        selectedBlockedUserName() {
            if (!this.selectedBlockedUser) {
                return 'this user';
            }

            return this.getUserName(
                this.selectedBlockedUser
            );
        },

        isConfirmationSubmitting() {
            return this.unblockingUserId !== null;
        }
    },

    mounted() {
        document.addEventListener(
            'keydown',
            this.handleDocumentKeydown
        );

        if (!this.blockUserData) {
            this.loadBlockedUsers();
        }
    },

    beforeDestroy() {
        document.removeEventListener(
            'keydown',
            this.handleDocumentKeydown
        );

        this.unlockBodyScroll();
    },

    methods: {
        goBack() {
            this.$router.go(-1);
        },

        async loadBlockedUsers() {
            if (this.isInitialLoading) {
                return;
            }

            this.isInitialLoading = true;
            this.loadError = '';
            this.additionalBlockedUsers = [];
            this.nextPageOverride = null;

            try {
                await this.$store.dispatch(
                    'getBlockUserFun'
                );
            } catch (error) {
                this.loadError = this.getErrorMessage(
                    error,
                    'Unable to load blocked users.'
                );
            } finally {
                this.isInitialLoading = false;
            }
        },

        async refreshBlockedUsers() {
            this.additionalBlockedUsers = [];
            this.nextPageOverride = null;

            try {
                await this.$store.dispatch(
                    'getBlockUserFun'
                );
            } catch (error) {
                toast.error(
                    this.getErrorMessage(
                        error,
                        'Unable to refresh blocked users.'
                    )
                );
            }
        },

        async loadMoreBlockedUsers() {
            if (
                !this.nextPageUrl ||
                this.isLoadingMore
            ) {
                return;
            }

            this.isLoadingMore = true;

            try {
                const response = await axios.get(
                    this.nextPageUrl
                );

                const pageData =
                    this.extractPaginationData(response);

                const newBlockedUsers =
                    pageData &&
                    Array.isArray(pageData.results)
                        ? pageData.results
                        : [];

                this.additionalBlockedUsers =
                    this.mergeBlockedUsers(
                        this.additionalBlockedUsers,
                        newBlockedUsers
                    );

                this.nextPageOverride =
                    pageData && pageData.next
                        ? pageData.next
                        : '';
            } catch (error) {
                toast.error(
                    this.getErrorMessage(
                        error,
                        'Unable to load more blocked users.'
                    )
                );
            } finally {
                this.isLoadingMore = false;
            }
        },

        openUnblockConfirmation(blockedItem) {
            if (
                !blockedItem ||
                blockedItem.id === null ||
                blockedItem.id === undefined ||
                this.isConfirmationSubmitting
            ) {
                return;
            }

            this.previouslyFocusedElement =
                document.activeElement;

            this.selectedBlockedItem = blockedItem;
            this.isConfirmationOpen = true;

            this.lockBodyScroll();

            this.$nextTick(() => {
                if (this.$refs.confirmButton) {
                    this.$refs.confirmButton.focus();
                }
            });
        },

        closeUnblockConfirmation() {
            if (this.isConfirmationSubmitting) {
                return;
            }

            this.isConfirmationOpen = false;
            this.selectedBlockedItem = null;

            this.unlockBodyScroll();

            this.$nextTick(() => {
                if (
                    this.previouslyFocusedElement &&
                    typeof this.previouslyFocusedElement
                        .focus === 'function'
                ) {
                    this.previouslyFocusedElement.focus();
                }

                this.previouslyFocusedElement = null;
            });
        },

        async confirmUnblockUser() {
            if (
                !this.selectedBlockedItem ||
                this.isConfirmationSubmitting
            ) {
                return;
            }

            const blockedItem =
                this.selectedBlockedItem;

            this.unblockingUserId = blockedItem.id;

            try {
                const response =
                    await this.$store.dispatch(
                        'unBlockUserFun',
                        {
                            id: blockedItem.id
                        }
                    );

                toast.success(
                    this.getSuccessMessage(
                        response,
                        'User unblocked successfully.'
                    )
                );

                this.removeAdditionalBlockedUser(
                    blockedItem.id
                );

                this.unblockingUserId = null;
                this.closeUnblockConfirmation();

                await this.refreshBlockedUsers();
            } catch (error) {
                toast.error(
                    this.getErrorMessage(
                        error,
                        'Unable to unblock the user.'
                    )
                );

                this.unblockingUserId = null;
            }
        },

        isUnblocking(blockedItemId) {
            if (
                this.unblockingUserId === null ||
                blockedItemId === null ||
                blockedItemId === undefined
            ) {
                return false;
            }

            return (
                String(this.unblockingUserId) ===
                String(blockedItemId)
            );
        },

        removeAdditionalBlockedUser(blockedItemId) {
            const normalizedId =
                String(blockedItemId);

            this.additionalBlockedUsers =
                this.additionalBlockedUsers.filter(
                    function (blockedItem) {
                        if (!blockedItem) {
                            return false;
                        }

                        return (
                            String(blockedItem.id) !==
                            normalizedId
                        );
                    }
                );
        },

        mergeBlockedUsers(firstList, secondList) {
            const firstItems =
                Array.isArray(firstList)
                    ? firstList
                    : [];

            const secondItems =
                Array.isArray(secondList)
                    ? secondList
                    : [];

            const mergedItems = [];
            const usedKeys = {};

            firstItems
                .concat(secondItems)
                .forEach((blockedItem, index) => {
                    if (!blockedItem) {
                        return;
                    }

                    const key =
                        this.getBlockedItemKey(
                            blockedItem,
                            index
                        );

                    if (usedKeys[key]) {
                        return;
                    }

                    usedKeys[key] = true;
                    mergedItems.push(blockedItem);
                });

            return mergedItems;
        },

        getBlockedItemKey(blockedItem, index) {
            if (!blockedItem) {
                return 'blocked-user-' + (index || 0);
            }

            if (
                blockedItem.id !== undefined &&
                blockedItem.id !== null
            ) {
                return 'blocked-request-' + blockedItem.id;
            }

            if (
                blockedItem.blocked_user &&
                blockedItem.blocked_user.id !==
                    undefined &&
                blockedItem.blocked_user.id !== null
            ) {
                return (
                    'blocked-user-' +
                    blockedItem.blocked_user.id
                );
            }

            return 'blocked-user-' + (index || 0);
        },

        getUserName(user) {
            if (!user) {
                return 'Unknown user';
            }

            const fullName = [
                user.salutation || '',
                user.first_name || '',
                user.last_name || ''
            ]
                .join(' ')
                .replace(/\s+/g, ' ')
                .trim();

            return (
                fullName ||
                user.email ||
                'Unknown user'
            );
        },

        getUserThumbnail(user) {
            if (
                user &&
                user.profile &&
                user.profile.thumbnail
            ) {
                return user.profile.thumbnail;
            }

            return DEFAULT_AVATAR;
        },

        handleAvatarError(event) {
            if (
                !event ||
                !event.target
            ) {
                return;
            }

            event.target.onerror = null;
            event.target.src = DEFAULT_AVATAR;
        },

        extractPaginationData(response) {
            if (
                response &&
                response.data &&
                response.data.data &&
                Array.isArray(
                    response.data.data.results
                )
            ) {
                return response.data.data;
            }

            if (
                response &&
                response.data &&
                Array.isArray(response.data.results)
            ) {
                return response.data;
            }

            return {
                results: [],
                next: ''
            };
        },

        getSuccessMessage(response, fallbackMessage) {
            if (response && response.data) {
                return (
                    response.data.msg ||
                    response.data.message ||
                    fallbackMessage
                );
            }

            return fallbackMessage;
        },

        getErrorMessage(error, fallbackMessage) {
            const responseData =
                error &&
                error.response &&
                error.response.data
                    ? error.response.data
                    : error && error.data
                        ? error.data
                        : null;

            if (responseData) {
                const nestedData =
                    responseData.data;

                if (
                    nestedData &&
                    Array.isArray(
                        nestedData.non_field_errors
                    ) &&
                    nestedData.non_field_errors.length
                ) {
                    return nestedData.non_field_errors[0];
                }

                if (
                    Array.isArray(
                        responseData.non_field_errors
                    ) &&
                    responseData.non_field_errors.length
                ) {
                    return responseData.non_field_errors[0];
                }

                return (
                    responseData.msg ||
                    responseData.message ||
                    fallbackMessage
                );
            }

            if (error && error.message) {
                return error.message;
            }

            return fallbackMessage;
        },

        handleDocumentKeydown(event) {
            if (
                event.key === 'Escape' &&
                this.isConfirmationOpen
            ) {
                this.closeUnblockConfirmation();
            }
        },

        lockBodyScroll() {
            document.body.classList.add(
                'body_right_scroll_remove'
            );
        },

        unlockBodyScroll() {
            document.body.classList.remove(
                'body_right_scroll_remove'
            );
        }
    }
};
</script>

<style lang="scss" scoped>
$primary-text: #ffffff;
$secondary-text: #a8a8a8;
$accent-color: #87cefa;
$danger-color: #ff3a30;
$highlight-color: #ffe738;
$table-background: rgba(228, 233, 237, 0.1);
$table-border: rgba(228, 233, 237, 0.22);

.blocked-users {
    color: $primary-text;
    font-family: 'MyriadProRegular', 'Lato', sans-serif;
    background: rgba(0, 0, 0, .6);
    border-radius: 3px;
    padding: 15px;
    min-height: calc(100dvh - 80px);
    position: relative;
    width: 100%;

    &__header {
        display: flex;
        align-items: center;
    }

    &__title {
        display: flex;
        align-items: center;
        margin: 0;
        color: $primary-text;
        font-size: 24px;
        font-weight: 700;
        text-transform: uppercase;
    }

    &__table-wrapper {
        position: relative;
        min-height: 180px;
        margin-top: 24px;
        padding-top: 4px;
    }

    &__table {
        width: 100%;
        margin-bottom: 0;
        border-spacing: 0 3px;
        border-collapse: separate;
        color: $primary-text;
        letter-spacing: 0.5px;

        th {
            padding: 10px 8px;
            border: 0;
            color: $accent-color;
            background-color: $table-background;
            font-family:
                'MyriadProRegular',
                'Lato',
                sans-serif;
            font-size: 19px;
            text-transform: uppercase;
            word-spacing: 5px;
        }

        td {
            padding: 16px 8px;
            border: 0;
            color: $primary-text;
            background-color: $table-background;
            font-family:
                'MyriadProRegular',
                'Lato',
                sans-serif;
            font-size: 18px;
            vertical-align: middle;
        }
    }

    &__action-cell {
        width: 160px;
    }

    &__unblock {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 105px;
        min-height: 36px;
        padding: 6px 12px;
        border: 1px solid $danger-color;
        border-radius: 3px;
        color: $primary-text;
        background-color: $danger-color;
        font-family: inherit;
        font-size: 14px;
        cursor: pointer;
        transition:
            color 0.2s ease,
            border-color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            margin-right: 6px;
        }

        &:hover:not(:disabled),
        &:focus:not(:disabled) {
            color: #000000;
            border-color: $accent-color;
            background-color: $accent-color;
        }

        &:focus {
            outline: 2px solid $highlight-color;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }
    }

    &__load-more-cell {
        padding: 12px 8px 0 !important;
        text-align: center;
        background-color: transparent !important;
    }

    &__load-more {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 125px;
        min-height: 38px;
        padding: 7px 15px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 3px;
        color: $primary-text;
        background-color: rgba(255, 255, 255, 0.1);
        font-family: inherit;
        cursor: pointer;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            margin-right: 7px;
        }

        &:hover:not(:disabled),
        &:focus:not(:disabled) {
            color: #22292f;
            background-color: $accent-color;
        }

        &:focus {
            outline: 2px solid $highlight-color;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }
    }

    &__empty {
        padding: 30px 15px !important;
        font-size: 20px !important;
        text-align: center;
    }

    &__loader,
    &__error {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        min-height: 180px;
        text-align: center;
    }

    &__error {
        color: #ff6b6b;
    }

    &__error-message {
        margin-bottom: 15px;
    }
}

.blocked-user {
    display: flex;
    align-items: center;
    min-width: 0;

    &__avatar {
        flex: 0 0 46px;
        width: 46px;
        height: 46px;
        margin-right: 12px;
        border-radius: 50%;
        background-color: rgba(255, 255, 255, 0.08);
        object-fit: cover;
    }

    &__name {
        min-width: 0;
        line-height: 1.3;
        word-break: break-word;
    }
}

.unblock-confirmation {
    position: fixed;
    inset: 0;
    z-index: 1100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    overflow-y: auto;
    background-color: rgba(0, 0, 0, 0.88);

    &__dialog {
        width: 100%;
        max-width: 480px;
        overflow: hidden;
        border-radius: 6px;
        color: $primary-text;
        background: #000000;
        box-shadow: 0 0 14px rgba(255, 213, 0, 0.55);
    }

    &__body {
        padding: 24px 20px;
        text-align: center;
    }

    &__user {
        display: flex;
        justify-content: center;
        margin-bottom: 15px;
    }

    &__avatar {
        width: 64px;
        height: 64px;
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 50%;
        background-color: rgba(255, 255, 255, 0.08);
        object-fit: cover;
    }

    &__message {
        margin: 0;
        color: $primary-text;
        font-size: 17px;
        line-height: 1.5;

        strong {
            color: $accent-color;
        }
    }

    &__description {
        margin: 10px 0 0;
        color: $secondary-text;
        font-size: 14px;
        line-height: 1.5;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 0 20px 20px;
    }

    &__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 115px;
        min-height: 40px;
        padding: 8px 14px;
        border: 0;
        border-radius: 4px;
        color: $primary-text;
        font-family: inherit;
        font-size: 15px;
        cursor: pointer;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease;

        .fa {
            margin-right: 7px;
        }

        &:focus {
            outline: 2px solid $highlight-color;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        &--cancel {
            background-color: rgba(255, 255, 255, 0.14);

            &:hover:not(:disabled){
                background-color: rgba(255, 255, 255, 0.25);
            }
        }

        &--confirm {
            background-color: $danger-color;

            &:hover:not(:disabled) {
                color: #000000;
                background-color: $accent-color;
            }
        }
    }
}

@media only screen and (max-width: 767px) {
    .blocked-users {
        &__title {
            font-size: 20px;
        }

        &__table {
            display: block;

            thead {
                position: absolute;
                width: 1px;
                height: 1px;
                overflow: hidden;
                clip: rect(0, 0, 0, 0);
            }

            tbody,
            tr,
            td {
                display: block;
                width: 100%;
            }

            tr {
                margin-bottom: 10px;
                overflow: hidden;
                border: 1px solid $table-border;
                border-radius: 5px;
            }

            td {
                position: relative;
                min-height: 48px;
                padding:
                    10px
                    10px
                    10px
                    42% !important;
                border-bottom: 1px solid rgba(
                    228,
                    233,
                    237,
                    0.08
                );
                font-size: 14px;

                &::before {
                    position: absolute;
                    top: 12px;
                    left: 10px;
                    width: 36%;
                    color: $accent-color;
                    content: attr(data-label);
                    font-size: 14px;
                    font-weight: 600;
                    text-align: left;
                    white-space: normal;
                }

                &:last-child {
                    border-bottom: 0;
                }

                &[colspan] {
                    padding-left: 10px !important;

                    &::before {
                        display: none;
                    }
                }
            }
        }

        &__action-cell {
            width: 100%;
            text-align: left !important;
        }

        &__load-more-cell {
            padding-left: 10px !important;
        }
    }

    .blocked-user {
        &__avatar {
            flex-basis: 40px;
            width: 40px;
            height: 40px;
        }
    }
}

@media only screen and (max-width: 575px) {
    .unblock-confirmation {
        &__dialog {
            max-width: 100%;
        }

        &__footer {
            flex-direction: column-reverse;
        }

        &__button {
            width: 100%;
        }
    }
}
</style>